export const C = {
  crimson:     "#8B1A1A",
  deep:        "#4A0A0A",
  rich:        "#A52020",
  light:       "#C23030",
  gold:        "#C9A84C",
  goldLight:   "#E2C472",
  goldDeep:    "#9E7C2A",
  goldPale:    "#FBF5E6",
  cream:       "#FDFAF4",
  offWhite:    "#F5EFE4",
  parchment:   "#EDE0C8",
  ink:         "#0D0404",
  inkMid:      "#2E1010",
  muted:       "#6B4E4E",
  border:      "#DFD0B0",
  sand:        "#F1E4CE",
  white:       "#FFFFFF",
  shadowSm:    "0 2px 14px rgba(74,10,10,0.06)",
  shadowMd:    "0 14px 44px rgba(74,10,10,0.10)",
  shadowLg:    "0 26px 74px rgba(74,10,10,0.15)",
};

export const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Outfit:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'Outfit', sans-serif; background: ${C.cream}; color: ${C.ink}; cursor: none; }
  a { cursor: none; }
  button { cursor: none; }
  ::selection { background: ${C.crimson}; color: ${C.gold}; }

  /* Custom cursor */
  #cursor-dot {
    position: fixed; top: 0; left: 0; width: 10px; height: 10px;
    background: ${C.crimson}; border-radius: 50%; pointer-events: none;
    z-index: 99999; transform: translate(-50%,-50%);
    transition: width 0.2s, height 0.2s, background 0.2s;
  }
  #cursor-ring {
    position: fixed; top: 0; left: 0; width: 36px; height: 36px;
    border: 1.5px solid ${C.crimson}; border-radius: 50%; pointer-events: none;
    z-index: 99998; transform: translate(-50%,-50%);
    transition: transform 0.12s ease, width 0.25s, height 0.25s, opacity 0.2s, border-color 0.35s ease;
    opacity: 0.5;
  }
  body:hover #cursor-ring { opacity: 1; }

  /* Ticker */
  @keyframes ticker { from { transform: translateX(0) } to { transform: translateX(-50%) } }
  @keyframes fadeUp { from { opacity:0; transform:translateY(32px) } to { opacity:1; transform:translateY(0) } }
  @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
  @keyframes float  { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-14px) } }
  @keyframes shimmer { 0% { background-position:-600px 0 } 100% { background-position:600px 0 } }
  @keyframes goldShine { 0% { background-position:0% 50% } 100% { background-position:200% 50% } }
  @keyframes drawLine { from { transform: scaleX(0) } to { transform: scaleX(1) } }
  @keyframes blink { 0%,100% { opacity:1 } 50% { opacity:0 } }

  /* Reveal on scroll */
  .reveal { opacity: 0; transform: translateY(36px); transition: opacity 0.75s ease, transform 0.75s ease; }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  .reveal-left { opacity: 0; transform: translateX(-36px); transition: opacity 0.75s ease, transform 0.75s ease; }
  .reveal-left.visible { opacity: 1; transform: translateX(0); }

  .skel { background: linear-gradient(90deg,#E8D5C0 25%,#F0E2CE 50%,#E8D5C0 75%); background-size:600px 100%; animation:shimmer 1.4s infinite; }

  @media(max-width:800px) { .desk-nav { display:none !important; } .mob-btn { display:flex !important; } }
  @media(min-width:801px) { .mob-btn { display:none !important; } }

  /* Hide custom cursor on touch / small screens */
  @media(pointer:coarse),(hover:none),(max-width:800px) {
    body, a, button { cursor: auto !important; }
    #cursor-dot, #cursor-ring { display: none !important; }
  }

  /* Hero logo: hide on mobile so it doesn't overlap text */
  @media(max-width:768px) { .hero-logo-bg { display:none !important; } }

  /* Two-column splits collapse on small screens */
  @media(max-width:768px) { .split-2col { grid-template-columns: 1fr !important; } }

  /* Layout helpers */
  .wrap { max-width: 1160px; margin: 0 auto; }
  .section { padding: 104px 5vw; }
  .section-sm { padding: 72px 5vw; }
  .card-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(300px,100%),1fr)); gap: 24px; }
  @media(max-width:768px) { .section { padding: 72px 6vw; } .section-sm { padding: 52px 6vw; } }

  /* Hero split: content column + logo panel */
  .hero-grid { display: grid; grid-template-columns: 1.05fr 0.95fr; min-height: 100vh; }
  @media(max-width:900px) { .hero-grid { grid-template-columns: 1fr; } .hero-logo-panel { min-height: 44vh; } }

  /* Bento grid */
  .bento { display: grid; grid-template-columns: repeat(6,1fr); gap: 16px; grid-auto-rows: minmax(200px,auto); }
  .bento-tile img { transition: transform .6s ease; }
  .bento-tile:hover img { transform: scale(1.06); }
  @media(max-width:900px) { .bento { grid-template-columns: repeat(2,1fr); } .bento > * { grid-column: auto !important; grid-row: auto !important; } }
  @media(max-width:560px) { .bento { grid-template-columns: 1fr; } }

  /* Unified button system */
  .btn { display:inline-flex; align-items:center; justify-content:center; gap:10px; font-family:'Outfit',sans-serif; font-size:12px; font-weight:600; letter-spacing:2px; text-transform:uppercase; padding:16px 34px; border:1px solid transparent; border-radius:3px; text-decoration:none; cursor:none; transition:transform .25s ease, box-shadow .25s ease, background .25s ease, color .25s ease, border-color .25s ease; }
  .btn-primary { background:linear-gradient(135deg, ${C.gold}, ${C.goldDeep}); color:${C.deep}; box-shadow:0 8px 30px rgba(201,168,76,0.30); }
  .btn-primary:hover { transform:translateY(-3px); box-shadow:0 16px 46px rgba(201,168,76,0.52); }
  .btn-secondary { background:${C.crimson}; color:${C.gold}; }
  .btn-secondary:hover { background:${C.deep}; transform:translateY(-3px); }
  .btn-ghost { background:transparent; border-color:${C.gold}55; color:${C.gold}; }
  .btn-ghost:hover { background:${C.gold}14; border-color:${C.gold}; }
  .btn-ghost-dark { background:transparent; border-color:${C.crimson}40; color:${C.crimson}; }
  .btn-ghost-dark:hover { background:${C.crimson}0D; border-color:${C.crimson}; }

  /* Warm card system (replaces the old dark-flip hover) */
  .card { background:${C.white}; border:1px solid ${C.border}; border-radius:16px; box-shadow:${C.shadowSm}; transition:transform .3s ease, box-shadow .3s ease, border-color .3s ease; }
  .card:hover { transform:translateY(-6px); box-shadow:${C.shadowMd}; border-color:${C.gold}; }

  /* Benefit tags */
  .tag { display:inline-flex; align-items:center; gap:8px; background:${C.goldPale}; border:1px solid ${C.border}; color:${C.deep}; padding:10px 18px; border-radius:999px; font-family:'Outfit',sans-serif; font-size:12px; font-weight:600; letter-spacing:0.4px; transition:background .25s, color .25s, border-color .25s, transform .25s; cursor:none; }
  .tag:hover { background:${C.crimson}; color:${C.gold}; border-color:${C.crimson}; transform:translateY(-2px); }

  /* Photo treatment: warm overlay plus hover zoom */
  .photo { position:relative; overflow:hidden; }
  .photo > img { display:block; width:100%; height:100%; object-fit:cover; transition:transform .6s ease; }
  .photo::after { content:''; position:absolute; inset:0; background:linear-gradient(155deg, rgba(74,10,10,0.10), rgba(201,168,76,0.08)); pointer-events:none; }
  .photo:hover > img { transform:scale(1.05); }

  /* Respect reduced-motion preferences */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation-duration:0.001ms !important; transition-duration:0.001ms !important; scroll-behavior:auto !important; }
  }
`;
